// PRD.315 — reported patient age, DOB-derived age, and reconciliation.
//
// WHY THIS MODULE EXISTS
// ----------------------
// Age arithmetic was duplicated verbatim across five screens (`computeAge` in
// PatientShellLayout, PatientDetailsDrawer, OpenPatientDrawer, AllPatientsPage,
// TodayPatientsPage, FavoritePatientsPage), plus a separate `formatPatientAge`
// for print and a naive `/ 365.25` approximation in DrawCharting. PRD §12 asks
// for one pure, birthday-aware, unit-tested helper instead.
//
// THE TWO KINDS OF AGE
// --------------------
// `birth_date` is authoritative: current age is DERIVED from it and is always
// correct. `age_reported` is an OBSERVATION — what someone told the clinic on
// `age_reported_at` — and must never be recomputed, incremented or overwritten
// on its own. A record saying "35, reported Aug 25 2026" still means 35; ten
// years later the UI says it was reported ten years ago rather than claiming 45
// (PRD §9).
//
// TIMEZONE
// --------
// `birth_date` is a calendar date (Postgres `date`, synced as 'YYYY-MM-DD'), not
// an instant. Parsing it with `new Date('1990-05-14')` yields UTC midnight,
// which is May 13 for anyone west of Greenwich — the pre-existing `computeAge`
// copies did exactly that and were a day off in the Americas. Here the DOB is
// read as literal calendar parts and compared against the reference's LOCAL
// calendar day, so the answer matches the wall calendar the clinician is
// looking at.
//
// This module is intentionally self-contained (no workspace-package imports) so
// it runs under the repo's node:test harness.

// ─── Bounds ───

/**
 * Upper bound for a reported age. The oldest verified human lived to 122, so
 * 150 rejects typos and pasted years (`1990`) while never rejecting a real
 * patient. Matches the spirit of the `< 200` guard already in elia-ai-core.
 */
export const MAX_REPORTED_AGE = 150;

// ─── Date parsing ───

interface CalendarDate {
  year: number;
  /** 1-12. */
  month: number;
  /** 1-31. */
  day: number;
}

/**
 * Read the leading 'YYYY-MM-DD' of a date or datetime string as literal
 * calendar parts. Returns null for anything else — deliberately strict, since a
 * silently mis-parsed birth date produces a confidently wrong age.
 */
function parseCalendarDate(value: string | null | undefined): CalendarDate | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** The reference instant's LOCAL calendar day — see the timezone note above. */
function toLocalCalendarDate(reference: Date): CalendarDate {
  return {
    year: reference.getFullYear(),
    month: reference.getMonth() + 1,
    day: reference.getDate(),
  };
}

/** Accepts either an instant or a 'YYYY-MM-DD' string as the reference day. */
function resolveReference(reference: Date | string | null | undefined): CalendarDate | null {
  if (!reference) return null;
  if (typeof reference === 'string') {
    const parsed = parseCalendarDate(reference);
    if (parsed) return parsed;
    const d = new Date(reference);
    return Number.isNaN(d.getTime()) ? null : toLocalCalendarDate(d);
  }
  return Number.isNaN(reference.getTime()) ? null : toLocalCalendarDate(reference);
}

// ─── Age from DOB ───

/**
 * Whole years completed between `birthDate` and `referenceDate`.
 *
 * Birthday-aware: the year difference is decremented until the birthday has
 * actually been reached, so it is never the naive `referenceYear - birthYear`
 * (PRD §12).
 *
 * A Feb-29 birth date counts as having its birthday on Mar 1 in non-leap years,
 * which is what this month/day comparison naturally yields and what the
 * screens' existing arithmetic already produced.
 *
 * Returns null when either date is missing/unparseable, and for a birth date in
 * the reference's future — a negative age is never a useful thing to display.
 */
export function calculateAgeOnDate(
  birthDate: string | null | undefined,
  referenceDate: Date | string | null | undefined,
): number | null {
  const dob = parseCalendarDate(birthDate);
  const ref = resolveReference(referenceDate);
  if (!dob || !ref) return null;

  let age = ref.year - dob.year;
  const birthdayReached = ref.month > dob.month || (ref.month === dob.month && ref.day >= dob.day);
  if (!birthdayReached) age -= 1;

  if (age < 0 || age > MAX_REPORTED_AGE) return null;
  return age;
}

// ─── Display resolution ───

/** The patient columns this module reads. Structural, so any row shape fits. */
export interface PatientAgeSource {
  birth_date?: string | null;
  age_reported?: number | string | null;
  age_reported_at?: string | null;
}

export interface PatientAgeDisplay {
  /** Whole years to display, or null when nothing is known. */
  years: number | null;
  /**
   * Where `years` came from:
   *  - 'dob'      — derived from birth_date, always current;
   *  - 'reported' — the stored observation, as of `reportedAt`;
   *  - null       — unknown.
   */
  source: 'dob' | 'reported' | null;
  /** When the reported age was captured. Only set when source === 'reported'. */
  reportedAt: string | null;
}

const UNKNOWN_AGE: PatientAgeDisplay = { years: null, source: null, reportedAt: null };

/** Coerce a stored age, which arrives as a number from PG and text from SQLite. */
function readStoredAge(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  if (n < 0 || n > MAX_REPORTED_AGE) return null;
  return n;
}

/**
 * Resolve which age a demographic surface should show (PRD §8).
 *
 * DOB wins whenever it is present and parseable, because it is the only source
 * that stays correct as time passes. Reported age is the fallback, and is
 * returned EXACTLY as stored — never aged forward. When neither is known the
 * caller gets nulls and keeps its existing unknown/empty state; no age is
 * invented.
 */
export function resolvePatientAgeDisplay(
  patient: PatientAgeSource | null | undefined,
  now: Date | string = new Date(),
): PatientAgeDisplay {
  if (!patient) return UNKNOWN_AGE;

  const fromDob = calculateAgeOnDate(patient.birth_date, now);
  if (fromDob !== null) return { years: fromDob, source: 'dob', reportedAt: null };

  const reported = readStoredAge(patient.age_reported);
  if (reported !== null) {
    return { years: reported, source: 'reported', reportedAt: patient.age_reported_at ?? null };
  }

  return UNKNOWN_AGE;
}

// ─── Input validation ───

export interface ParsedReportedAge {
  /** The accepted age, or null when the field was left blank / cleared. */
  value: number | null;
  /** Human-readable reason the input was rejected, or null when acceptable. */
  error: string | null;
}

/**
 * Validate a reported-age input (PRD §6.1).
 *
 * Blank means "not reported", not zero. Decimals and stray text are REJECTED
 * rather than coerced — silently turning "thirty" or "35.7" into a number would
 * fabricate a demographic value.
 */
export function parseReportedAge(raw: string | number | null | undefined): ParsedReportedAge {
  if (raw === null || raw === undefined) return { value: null, error: null };
  const text = String(raw).trim();
  if (!text) return { value: null, error: null };

  if (!/^\d+$/.test(text)) {
    return { value: null, error: 'Age must be a whole number' };
  }
  const n = Number(text);
  if (!Number.isFinite(n)) return { value: null, error: 'Age must be a whole number' };
  if (n > MAX_REPORTED_AGE) {
    return { value: null, error: `Age must be ${MAX_REPORTED_AGE} or less` };
  }
  return { value: n, error: null };
}

// ─── Write resolution ───

export interface ReportedAgeWrite {
  age_reported: number | null;
  age_reported_at: string | null;
}

/**
 * Decide what to persist for the reported-age pair (PRD §5.2, §6.3).
 *
 * The provenance timestamp refreshes ONLY when the value actually changes, so
 * editing an unrelated demographic field does not silently restamp the age as
 * freshly reported. Clearing the age clears its timestamp — a reporting date
 * with nothing reported is meaningless. An unchanged age keeps its original
 * timestamp, even if that timestamp was previously missing on a legacy row.
 */
export function resolveReportedAgeWrite(params: {
  previousAge: number | string | null | undefined;
  previousReportedAt: string | null | undefined;
  nextAge: number | null;
  now: string;
}): ReportedAgeWrite {
  const previous = readStoredAge(params.previousAge);

  if (params.nextAge === null) return { age_reported: null, age_reported_at: null };

  if (previous === params.nextAge) {
    return { age_reported: params.nextAge, age_reported_at: params.previousReportedAt ?? null };
  }

  return { age_reported: params.nextAge, age_reported_at: params.now };
}

// ─── Reconciliation ───

export type ReportedAgeCheck =
  | { status: 'ok' }
  | {
      status: 'mismatch';
      /** What was reported. */
      reportedAge: number;
      /** When it was reported (ISO). */
      reportedAt: string;
      /** What the entered DOB implies the age WAS on that date. */
      dobAgeAtReport: number;
    };

/**
 * Compare a reported age against a birth date (PRD §10).
 *
 * The comparison is made **on `age_reported_at`**, not today. Someone who was
 * 35 on Mar 1 2026 with a DOB of May 14 1990 is perfectly consistent even
 * though they are 36 by August — comparing against today's age would fire a
 * false conflict on almost every record whose birthday has since passed.
 *
 * Returns 'ok' whenever there is nothing to reconcile: no DOB, no reported age,
 * no reporting timestamp, or an unparseable value. This check exists to warn a
 * human, never to gate a save on missing data.
 */
export function checkReportedAgeAgainstDob(params: {
  birthDate: string | null | undefined;
  ageReported: number | string | null | undefined;
  ageReportedAt: string | null | undefined;
}): ReportedAgeCheck {
  const reportedAge = readStoredAge(params.ageReported);
  const reportedAt = params.ageReportedAt ?? null;
  if (reportedAge === null || !reportedAt) return { status: 'ok' };

  const dobAgeAtReport = calculateAgeOnDate(params.birthDate, reportedAt);
  if (dobAgeAtReport === null) return { status: 'ok' };

  if (dobAgeAtReport === reportedAge) return { status: 'ok' };
  return { status: 'mismatch', reportedAge, reportedAt, dobAgeAtReport };
}
